import React, { useCallback, useEffect, useRef, useState } from 'react';
import AgenteIcono from './AgenteIcono';
import { useNavigate } from 'react-router';
import { sonarCampana } from '../lib/sonidos';
import { api, formatDateTime } from '../lib/api';
import { avisoDiaDeConsulta, avisoEnlacesPacientes } from '../lib/saludEnlaces';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Bell, ClipboardCheck, ClipboardList, BookOpen, Check, Link2 } from 'lucide-react';

/**
 * Campana de avisos del Agente (2026-07-25). Globo rojo con los avisos sin
 * ver (quejas, peticiones de cita…); abrirla los marca como vistos y desde
 * ahí se salta al chat del Agente.
 *
 * 2026-07-28: la ve TODO el personal —las novedades del correo son de todos—
 * y además enseña LAS TAREAS PROPIAS sin hacer. Lo leído se guarda por
 * persona: antes era global y en cuanto uno la abría se apagaba para todos,
 * que es por lo que parecía siempre lo mismo.
 *
 * 2026-09-06: también vigila los ENLACES DE PACIENTES (Dirección: «ha habido
 * fallos»). Si la comprobación diaria falló, o lleva dos días laborables
 * sin hacerse, sale en rojo y lleva a /salud-enlaces.
 *
 * 2026-09-10: y el DÍA DE CONSULTA, el recorrido de recepción (Dirección: «para
 * detectar los fallos anticipadamente… y si hay alguno informa rápido»), con
 * la misma regla y llevando a su pestaña.
 */
export default function AvisosBell() {
  const sonadoRef = useRef(null);   // último total con el que sonó la campana
  const [datos, setDatos] = useState({ items: [], pendientes: 0, tareas: [], tareas_pendientes: 0, protocolos: [], protocolos_pendientes: 0 });
  const [enlaces, setEnlaces] = useState(undefined);   // undefined = aún no leído
  const [recepcion, setRecepcion] = useState(undefined);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Hasta que no llega la respuesta no hay aviso: si no, cada carga de la
  // app enseñaría un rojo falso durante un instante.
  const avisoEnlaces = enlaces === undefined ? null : avisoEnlacesPacientes(enlaces);
  const avisoRecepcion = recepcion === undefined ? null : avisoDiaDeConsulta(recepcion);

  const cargar = useCallback(() => {
    api.get('/api/agente/avisos')
      .then((r) => {
        const d = r.data || { items: [], pendientes: 0, tareas: [], tareas_pendientes: 0, protocolos: [], protocolos_pendientes: 0 };
        // CAMPANA con sonido (2026-08-19): suena al entrar/recargar
        // si hay avisos sin ver, y cada vez que el total SUBE. Nunca en bucle:
        // solo cuando el número crece respecto a lo último que se oyó.
        const total = (d.pendientes || 0) + (d.tareas_pendientes || 0) + (d.protocolos_pendientes || 0);
        if (total > (sonadoRef.current ?? -1) && total > 0) sonarCampana();
        sonadoRef.current = total;
        setDatos(d);
      })
      .catch(() => {});
    api.get('/api/salud/enlaces')
      .then((r) => setEnlaces(r.data))
      .catch(() => {});
    api.get('/api/salud/recepcion')
      .then((r) => setRecepcion(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    cargar();
    const id = setInterval(() => { if (!document.hidden) cargar(); }, 60000);
    return () => clearInterval(id);
  }, [cargar]);

  // Archivar: el aviso desaparece de la campana para siempre. "Visto" solo
  // apaga el globo rojo; una queja resuelta seguía ahí semanas (2026-07-28).
  const archivar = (id) => {
    api.post(`/api/agente/avisos/${id}/archivar`)
      .then(() => setDatos((d) => ({ ...d, items: d.items.filter((x) => x.id !== id) })))
      .catch(() => {});
  };

  const alAbrir = (v) => {
    setOpen(v);
    if (v && datos.pendientes > 0) {
      api.post('/api/agente/avisos/visto')
        .then(() => setDatos((d) => ({ ...d, pendientes: 0 })))
        .catch(() => {});
    }
  };

  const total = (datos.pendientes || 0) + (datos.tareas_pendientes || 0) + (datos.protocolos_pendientes || 0)
    + (avisoEnlaces ? 1 : 0) + (avisoRecepcion ? 1 : 0);

  return (
    <Popover open={open} onOpenChange={alAbrir}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative shrink-0"
          title="Novedades del correo y tus tareas pendientes" data-testid="avisos-bell">
          <Bell className="w-5 h-5" />
          {total > 0 && (
            <span
              className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="avisos-count">
              {total > 9 ? '9+' : total}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(92vw,22rem)] p-0" data-testid="avisos-panel">
        <div className="px-3 py-2 border-b border-border flex items-center gap-2">
          <AgenteIcono size={22} />
          <span className="font-display font-semibold text-sm text-[#13231E]">
            Avisos del Agente
          </span>
        </div>
        {avisoEnlaces && (
          <div className="border-b border-border" data-testid="avisos-enlaces">
            <button type="button"
              onClick={() => { setOpen(false); navigate('/salud-enlaces'); }}
              className="w-full text-left px-3 py-2 bg-red-50 hover:bg-red-100"
              data-testid="avisos-enlaces-item">
              <div className="flex items-center gap-2">
                <Link2 className="w-3.5 h-3.5 text-red-700 shrink-0" />
                <span className="text-xs font-semibold text-red-900">{avisoEnlaces.texto}</span>
              </div>
              <div className="text-[10px] text-red-800/80 mt-0.5">
                Pulsa para ver el detalle y volver a comprobar
              </div>
            </button>
          </div>
        )}
        {avisoRecepcion && (
          <div className="border-b border-border" data-testid="avisos-recepcion">
            <button type="button"
              onClick={() => { setOpen(false); navigate('/salud-enlaces?vista=recepcion'); }}
              className="w-full text-left px-3 py-2 bg-red-50 hover:bg-red-100"
              data-testid="avisos-recepcion-item">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="w-3.5 h-3.5 text-red-700 shrink-0" />
                <span className="text-xs font-semibold text-red-900">{avisoRecepcion.texto}</span>
              </div>
              <div className="text-[10px] text-red-800/80 mt-0.5">
                Pulsa para ver el detalle y volver a comprobar
              </div>
            </button>
          </div>
        )}
        {datos.tareas_pendientes > 0 && (
          <div className="border-b border-border" data-testid="avisos-tareas">
            <div className="px-3 py-1.5 flex items-center gap-2 bg-amber-50">
              <ClipboardList className="w-3.5 h-3.5 text-amber-700" />
              <span className="text-xs font-semibold text-amber-900">
                Tus tareas pendientes ({datos.tareas_pendientes})
              </span>
            </div>
            <ul className="divide-y divide-border max-h-[28vh] overflow-y-auto">
              {datos.tareas.map((t) => (
                <li key={t.id}>
                  <button type="button"
                    onClick={() => { setOpen(false); navigate('/tareas-direccion'); }}
                    className="w-full text-left px-3 py-2 hover:bg-muted/40"
                    data-testid="avisos-tarea">
                    <div className="text-xs text-[#13231E] line-clamp-2">{t.texto}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {t.prioridad === 'alta' ? '🔴 Alta' : t.prioridad === 'baja' ? 'Baja' : 'Media'}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {datos.protocolos_pendientes > 0 && (
          <div className="border-b border-border" data-testid="avisos-protocolos">
            <div className="px-3 py-1.5 flex items-center gap-2 bg-red-50">
              <BookOpen className="w-3.5 h-3.5 text-red-700" />
              <span className="text-xs font-semibold text-red-900">
                Protocolos por leer ({datos.protocolos_pendientes})
              </span>
            </div>
            <ul className="divide-y divide-border max-h-[28vh] overflow-y-auto">
              {datos.protocolos.map((p) => (
                <li key={p.id}>
                  <button type="button"
                    onClick={() => { setOpen(false); navigate('/protocolos'); }}
                    className="w-full text-left px-3 py-2 hover:bg-muted/40"
                    data-testid="avisos-protocolo">
                    <div className="text-xs text-[#13231E] line-clamp-2">{p.texto}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Pulsa para leerla y confirmarla
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {datos.items.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            Sin avisos por ahora. El Agente vigila el correo cada minuto.
          </div>
        ) : (
          <ul className="divide-y divide-border max-h-[50vh] overflow-y-auto">
            {datos.items.map((a) => (
              <li key={a.id} className="flex items-start hover:bg-muted/40">
                <button type="button"
                  onClick={() => { setOpen(false); navigate('/agente'); }}
                  className="flex-1 min-w-0 text-left px-3 py-2"
                  data-testid="avisos-item">
                  <div className="text-xs text-[#13231E] line-clamp-3">{a.texto}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {formatDateTime(a.created_at)}
                  </div>
                </button>
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); archivar(a.id); }}
                  title="Ya está atendido: quitarlo de aquí"
                  className="shrink-0 mt-2 mr-2 p-1 rounded text-muted-foreground hover:text-emerald-700 hover:bg-emerald-50"
                  data-testid="avisos-archivar">
                  <Check className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="p-2 border-t border-border">
          <Button size="sm" className="w-full bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            onClick={() => { setOpen(false); navigate('/agente'); }}
            data-testid="avisos-abrir-agente">
            Abrir el Agente
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
